import { QUOTES } from "../data/quotes";
import { parseDay, todayStr, weekDays, weekKey } from "./date";
import { dayKcal } from "./energy";
import { dayFull, stats, weekCount } from "./stats";
import type { ChallengeDef, ChallengeState, KayaState, LevelInfo, XpSource } from "./types";

export const LEVELS: [number, string][] = [
  [0, "Seedling"],
  [300, "Walker"],
  [800, "Mover"],
  [1500, "Builder"],
  [2500, "Steady"],
  [4000, "Grinder"],
  [6000, "Athlete"],
  [8500, "Machine"],
  [12000, "Iron"],
  [16000, "Legend"],
];

/**
 * Where the XP came from, line by line.
 *
 * `xp` is the sum of this rather than its own arithmetic, so the number on Home and the list that
 * explains it cannot drift apart. The level was previously a bar with no way to find out what
 * moved it — which made it decoration rather than feedback.
 */
export function xpBreakdown(S: KayaState): XpSource[] {
  const st = stats(S);
  const rescues = Object.values(S.logs).filter((L) => L.rescued).length;
  const extras = Object.values(S.logs).reduce((a, L) => a + (L.extras || []).length, 0);

  const rows: Omit<XpSource, "total">[] = [
    { id: "sessions", label: "Sessions finished", each: 100, count: st.sessions },
    { id: "treadmills", label: "Treadmill walks", each: 60, count: st.treadmills },
    { id: "doubles", label: "Days with both", each: 40, count: st.doubles },
    { id: "extras", label: "Extra moves", each: 40, count: extras },
    { id: "measures", label: "Weights and waists logged", each: 10, count: st.weighIns + st.waistIns },
    { id: "badges", label: "Badges earned", each: 50, count: Object.keys(S.badges).length },
    { id: "rescues", label: "Days rescued", each: 50, count: rescues },
    { id: "challenges", label: "Weekly challenges", each: 200, count: Object.keys(S.challenges).length },
  ];
  return rows.map((r) => ({ ...r, total: r.each * r.count }));
}

export function xp(S: KayaState): number {
  return xpBreakdown(S).reduce((a, r) => a + r.total, 0);
}

export function level(S: KayaState): LevelInfo {
  const x = xp(S);
  let i = 0;
  while (i + 1 < LEVELS.length && x >= LEVELS[i + 1][0]) i++;
  const next = LEVELS[i + 1];
  return {
    n: i + 1,
    name: LEVELS[i][1],
    xp: x,
    from: LEVELS[i][0],
    to: next ? next[0] : null,
    pct: next ? (x - LEVELS[i][0]) / (next[0] - LEVELS[i][0]) : 1,
  };
}

interface ChallengeSpec extends ChallengeDef {
  prog: (S: KayaState) => [number, number];
}

export const CHALLENGES: ChallengeSpec[] = [
  {
    id: "clean",
    n: "Clean week",
    d: "All 6 scheduled sessions Mon–Sat.",
    prog: (S) => [weekCount(S), 6],
  },
  {
    id: "double",
    n: "Double up",
    d: "Three days with both a session and the treadmill.",
    prog: (S) => [
      weekDays().filter((ds) => dayFull(S, ds) && S.logs[ds] && S.logs[ds].treadmill).length,
      3,
    ],
  },
  {
    id: "walker",
    n: "Five walks",
    d: "Log the treadmill five times this week.",
    prog: (S) => [weekDays().filter((ds) => S.logs[ds] && S.logs[ds].treadmill).length, 5],
  },
  {
    id: "early",
    n: "Early three",
    d: "Finish three sessions before 8 am.",
    prog: (S) => [
      weekDays().filter((ds) => {
        const L = S.logs[ds];
        return !!L && !!L.completedAt && new Date(L.completedAt).getHours() < 8;
      }).length,
      3,
    ],
  },
  {
    id: "measure",
    n: "Measure up",
    d: "Log both weight and waist this week.",
    prog: (S) => {
      const w = S.measures.filter((m) => weekDays().includes(m.date));
      return [(w.some((m) => m.w) ? 1 : 0) + (w.some((m) => m.waist) ? 1 : 0), 2];
    },
  },
  {
    id: "burn",
    n: "Burn 1,200",
    d: "1,200 estimated kcal this week.",
    prog: (S) => [Math.min(1200, weekDays().reduce((a, ds) => a + dayKcal(S, ds), 0)), 1200],
  },
];

/** One challenge per week, rotating deterministically so every device agrees. */
export function challenge(S: KayaState): ChallengeState {
  const wk = weekKey();
  const c = CHALLENGES[Math.floor(parseDay(wk).getTime() / 604800000) % CHALLENGES.length];
  const [a, n] = c.prog(S);
  return { c, a, n, done: !!S.challenges[wk], wk };
}

/** Streak freezes: +1 per clean week, +1 per 10 sessions, capped at 3.
 *  Returns the keys newly earned so the caller can persist and toast once. */
export function earnableFreezes(S: KayaState): string[] {
  const st = stats(S);
  const out: string[] = [];

  const byWeek: Record<string, number> = {};
  Object.keys(S.logs)
    .filter((ds) => dayFull(S, ds))
    .forEach((ds) => {
      const k = "w" + weekKey(parseDay(ds));
      byWeek[k] = (byWeek[k] || 0) + 1;
    });
  Object.keys(byWeek).forEach((k) => {
    if (byWeek[k] >= 6 && !S.freezeEarned[k]) out.push(k);
  });

  for (let t = 10; t <= st.sessions; t += 10) {
    const k = "s" + t;
    if (!S.freezeEarned[k]) out.push(k);
  }
  return out;
}

export function quote(): string {
  const i = Math.floor(Date.now() / 864e5) % QUOTES.length;
  return QUOTES[i];
}

export { todayStr };
